import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaPaperPlane, FaWhatsapp } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const ContactPageContainer = styled.div`
  padding: 8rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  color: #1f2937;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const SectionSubtitle = styled.p`
  font-size: 1.4rem;
  color: #4b5563;
  margin-bottom: 4rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  height: 100%;
  justify-content: space-between;
`;

const ContactCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1.5rem;

  svg {
    font-size: 2rem;
    color: #00B4DB;
  }

  div {
    h3 {
      font-size: 1.2rem;
      color: #1f2937;
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 1.1rem;
      color: #4b5563;
    }
  }
`;

const ContactForm = styled.form`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
`;

const MainContactForm = styled.form`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;

  &:last-of-type {
    margin-bottom: 3rem;
    flex: 1;
  }

  label {
    display: block;
    font-size: 1.1rem;
    color: #1f2937;
    margin-bottom: 0.3rem;
  }

  input, textarea {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 1rem;
    color: #1f2937;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: #00B4DB;
      box-shadow: 0 0 0 2px rgba(0, 180, 219, 0.1);
    }
  }

  textarea {
    min-height: 120px;
    resize: none;
    height: 100%;
  }
`;

const ContactInput = styled.input`
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const SubmitButton = styled(motion.button)`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);
  height: 100%;
  min-height: 48px;

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-top: 4rem;
  margin-bottom: 4rem;
`;

const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;
  margin-top: 8rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 100%;
`;

const ContactTitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 2rem;
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #00B4DB 0%, #0083B0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;

function ContactPage() {
  const [mainFormData, setMainFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  const [quickContactData, setQuickContactData] = useState({
    name: '',
    phone: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleMainFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setMainFormData({
          ...mainFormData,
          [name]: numbersOnly
        });
      }
    } else {
      setMainFormData({
        ...mainFormData,
        [name]: value
      });
    }
  };

  const handleQuickContactChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setQuickContactData({
          ...quickContactData,
          [name]: numbersOnly
        });
      }
    } else {
      setQuickContactData({
        ...quickContactData,
        [name]: value
      });
    }
  };

  const handleMainFormSubmit = async (e) => {
    e.preventDefault();
    
    if (mainFormData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: mainFormData.name,
        phone: mainFormData.phone,
        email: mainFormData.email,
        message: mainFormData.message,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_4ifmu2r',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setMainFormData({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  const handleQuickContactSubmit = async (e) => {
    e.preventDefault();
    
    if (quickContactData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: quickContactData.name,
        phone: quickContactData.phone,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_w676du8',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setQuickContactData({ name: '', phone: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  return (
    <ContactPageContainer>
      <SectionTitle>İletişim</SectionTitle>
      <SectionSubtitle>
        Bizimle iletişime geçin, dijital dünyada markanızı birlikte büyütelim.
      </SectionSubtitle>

      <MapContainer>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d192230.12667749848!2d28.70858771609813!3d41.16517199581645!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab264ecd61265%3A0x9b084a042b7d4607!2zRXnDvHBzdWx0YW4vxLBzdGFuYnVs!5e0!3m2!1str!2str!4v1749899029407!5m2!1str!2str"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </MapContainer>

      <ContactGrid>
        <ContactInfo>
          <ContactCard
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <FaPhone />
            <div>
              <h3>Telefon</h3>
              <p>+90 850 933 4655</p>
            </div>
          </ContactCard>

          <ContactCard
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <FaWhatsapp />
            <div>
              <h3>WhatsApp</h3>
              <p>+90 532 574 3379</p>
            </div>
          </ContactCard>

          <ContactCard
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <FaEnvelope />
            <div>
              <h3>E-posta</h3>
              <p>demirerajans@gmail.com</p>
            </div>
          </ContactCard>

          <ContactCard
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <FaMapMarkerAlt />
            <div>
              <h3>Adres</h3>
              <p>Eyüpsultan, İstanbul</p>
            </div>
          </ContactCard>
        </ContactInfo>

        <MainContactForm id="mainContactForm" onSubmit={handleMainFormSubmit}>
          <FormTitle>İletişim Formu</FormTitle>
          <FormGroup>
            <label htmlFor="name">Adınız</label>
            <input 
              type="text" 
              id="name" 
              placeholder="Adınızı giriniz" 
              name="name" 
              value={mainFormData.name} 
              onChange={handleMainFormChange} 
              required 
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="phone">Telefon</label>
            <input 
              type="tel" 
              id="phone" 
              placeholder="Telefon Numaranız (11 Haneli)" 
              name="phone" 
              value={mainFormData.phone} 
              onChange={handleMainFormChange} 
              pattern="[0-9]*" 
              inputMode="numeric" 
              maxLength="11" 
              minLength="11" 
              required 
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="email">E-posta</label>
            <input 
              type="email" 
              id="email" 
              placeholder="E-posta adresinizi giriniz" 
              name="email"
              value={mainFormData.email}
              onChange={handleMainFormChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="message">Mesajınız</label>
            <textarea 
              id="message" 
              placeholder="Mesajınızı giriniz"
              name="message"
              value={mainFormData.message}
              onChange={handleMainFormChange}
              required
            ></textarea>
          </FormGroup>

          <SubmitButton
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaPaperPlane />
            Gönder
          </SubmitButton>
        </MainContactForm>
      </ContactGrid>

      <ContactSection>
        <ContactContent>
          <ContactTitle>Sizi Arayalım</ContactTitle>
          <ContactForm id="quickContactForm" onSubmit={handleQuickContactSubmit}>
            <ContactInput 
              type="text" 
              name="name"
              placeholder="Adınız"
              value={quickContactData.name}
              onChange={handleQuickContactChange}
              required 
            />
            <ContactInput 
              type="tel" 
              name="phone"
              placeholder="Telefon Numaranız (11 Haneli)"
              value={quickContactData.phone}
              onChange={handleQuickContactChange}
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              minLength="11"
              required 
            />
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </ContactPageContainer>
  );
}

export default ContactPage; 